<template>
  <div
    v-click-outside="closeDropdown"
    :class="['dropdown-container', { disabled, label: !field.value }]"
  >
    <div class="dropdown" :class="{ open: dropdownOpen }" @click="toggleDropdown">
      <div :class="['dropdown-selected', { required: showRequired }]">{{ value }}</div>
      <FriedIcon
        v-if="!disabled"
        :class="['dropdown-arrow', { rotate: dropdownOpen }]"
        icon="chevron-down"
      />
    </div>

    <transition name="dropdown-options">
      <div v-if="dropdownOpen" class="dropdown-options-container">
        <div
          v-for="option in options"
          :key="option"
          :class="['option', { 'placeholder-value': isDefaultValue(option) }]"
          @click="handleSelection(option)"
        >
          {{ option }}
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import type { PropType, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Field } from '../types/field';
import { FieldSource } from '../types/enums/field-source';
import { defaultDropdownOption } from '../constants/field-placeholders';

export default defineComponent({
  name: 'DropdownField',
  props: {
    field: { type: Object as PropType<Field>, required: true },
    disabled: { type: Boolean, required: true },
    completed: { type: Boolean, required: true },
  },
  emits: ['update', 'toggle-dropdown'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const dropdownOpen = ref(false);

    const value = computed(() => {
      if (props.field.source === FieldSource.Sender) {
        return props.field.value || props.field.label;
      }
      return props.field.value || props.field.label || defaultOption.value;
    });

    const showRequired: ComputedRef<boolean> = computed(() => {
      if (props.completed || props.disabled) {
        return false;
      }
      return props.field.isRequired;
    });

    const options: ComputedRef<string[]> = computed(() => {
      if (isDefaultValue(value.value as string) || props.field.isRequired) {
        return props.field.options;
      }
      return [defaultOption.value, ...props.field.options];
    });

    const defaultOption: ComputedRef<string> = computed(() => t(defaultDropdownOption).toString());

    const isDefaultValue = (value: string) => value === defaultOption.value;

    const handleSelection = (value: string) => {
      if (!props.disabled) {
        emit('update', value);
        closeDropdown();
      }
    };

    const toggleDropdown = () => {
      if (props.disabled) {
        return;
      }
      dropdownOpen.value = !dropdownOpen.value;
      emit('toggle-dropdown', dropdownOpen.value);
    };

    const closeDropdown = () => {
      dropdownOpen.value = false;
      emit('toggle-dropdown', dropdownOpen.value);
    };

    return {
      dropdownOpen,
      value,
      showRequired,
      options,
      defaultOption,
      isDefaultValue,
      handleSelection,
      toggleDropdown,
      closeDropdown,
    };
  },
});
</script>
<style lang="scss" scoped>
.dropdown-container {
  user-select: none;
  width: 100%;
  height: inherit;
  position: absolute;
  top: 0;
  left: 0;
  font-size: 1em;
  font-weight: normal;
  z-index: $z-base;

  &.label {
    color: var(--text-gray);
  }

  .dropdown-options-container {
    max-height: 11rem;
    overflow: scroll;
    border: solid 1px var(--text-dark);
    border-radius: var(--radius-small);
    margin-top: 1px;
    font-size: 1em;
    background: var(--white);

    .option {
      cursor: pointer;
      padding: 1em 0.5em;
      background-color: var(--white);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.placeholder-value {
        color: var(--text-gray);
      }

      &:hover {
        background-color: var(--gray-98);
      }
    }
  }

  &.disabled .dropdown-options-container {
    border-color: var(--gray-96);

    .option {
      pointer-events: none;
    }
  }

  .dropdown {
    cursor: pointer;
    height: inherit;
    display: flex;
    align-items: center;
    padding-left: 0.25em;
    padding-right: 2em;

    .dropdown-selected {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      height: inherit;
      line-height: 1.7;

      &.required::after {
        content: '*';
        color: var(--error-dark);
        margin-left: 2px;
      }
    }

    .dropdown-arrow {
      position: absolute;
      right: 0.7em;
      width: auto;
      height: 20%;
      transition: transform 0.25s ease-in-out;

      &.rotate {
        transform: rotate(180deg);
      }
    }
  }

  .dropdown-options-enter-from,
  .dropdown-options-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .dropdown-options-enter-active,
  .dropdown-options-leave-active {
    transition: all 0.25s ease-in-out;
  }
}
</style>
